import { DEFAULT_LOCALE, LOCALES } from '@lib/constants';

/**
 * Utility functions for managing locale in localStorage with validation and fallbacks
 */

/**
 * Gets the current locale from localStorage with validation
 * @returns {string} The current valid locale or default locale
 */
export function getStoredLocale() {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE;
  }
  try {
    const storedLocale = localStorage.getItem('locale');
    const supportedLocales = LOCALES.split(',');

    // Return stored locale if valid, otherwise return default
    if (storedLocale && supportedLocales.includes(storedLocale)) {
      return storedLocale;
    }
    return DEFAULT_LOCALE;
  } catch (error) {
    console.warn('Failed to get locale from localStorage:', error);
    return DEFAULT_LOCALE;
  }
}

/**
 * Sets the locale in localStorage with validation
 * @param {string} locale - The locale to store
 * @returns {boolean} True if successfully stored, false otherwise
 */
export function setStoredLocale(locale) {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const supportedLocales = LOCALES.split(',');
    const validLocale = supportedLocales.includes(locale) ? locale : DEFAULT_LOCALE;
    localStorage.setItem('locale', validLocale);

    // Also set the cookie for server-side consistency
    const cookieOptions = ["NEXT_LOCALE=".concat(validLocale), 'path=/', "max-age=".concat(60 * 60 * 24 * 365),
    // 1 year
    'SameSite=Lax',
    // Add Secure flag in production
    ...(window.location.protocol === 'https:' ? ['Secure'] : [])];
    document.cookie = cookieOptions.join('; ');
    return true;
  } catch (error) {
    console.warn('Failed to set locale in localStorage:', error);
    return false;
  }
}

/**
 * Ensures the current locale is synchronized between URL, localStorage, and cookies
 * @param {string} currentLocale - The current locale from the URL or context
 */
export function syncLocaleStorage(currentLocale) {
  const storedLocale = getStoredLocale();
  const supportedLocales = LOCALES.split(',');
  const validCurrentLocale = supportedLocales.includes(currentLocale) ? currentLocale : DEFAULT_LOCALE;

  // If stored locale doesn't match current locale, update it
  if (storedLocale !== validCurrentLocale) {
    setStoredLocale(validCurrentLocale);
  }
}

/**
 * Validates if a locale is supported
 * @param {string} locale - The locale to validate
 * @returns {boolean} True if the locale is supported
 */
export function isValidLocale(locale) {
  const supportedLocales = LOCALES.split(',');
  return supportedLocales.includes(locale);
}